import React, {FunctionComponent, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {
    ACard,
    ADataGrid, ADataGridColors,
    ADataGridColumn,
    ADataGridFilter, ASectionTitle,
    PagedSearchParams, EmptyState
} from "@atiautomacao/ati-ui-library";
import { extractFiltersFromColumns } from "../../../../Utils/DataUitils";
import { RootState } from "../../../../Config/Store";
import { useAppDispatch, useAppSelector } from "../../../../Config/Hooks";
import {searchEntities, Alarm, clear} from "../Alarms/AlarmPowerPlantReducer";
import {isArray} from "lodash";
import {DateFormat, LocalDateTimeFormatISO, validateDateFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {Box, Button, Link, useTheme} from "@mui/material";
import {usePowerStationNavigation} from "../../../../Shared/Hooks/usePowerStationNavigation";
import {useSkidNavigation} from "../../../../Shared/Hooks/useSkidNavigation";
import {
    getAlarmDisplayLabel,
    handleOnColorWithPill,
    INHIBITED_ALARM_COLOR,
    mergeFiltersWithTotalizerAndPowerStation
} from "../../../../Utils/AlarmsService";
import {useSnackbar} from "notistack";
import {isValid} from "date-fns";
import {Storage} from "../../../../Utils/StorageUtils";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {useEquipmentNavigation} from "../../../../Shared/Hooks/useEquipmentNavigation";
import {useEquipmentWithoutSkidNavigation} from "../../../../Shared/Hooks/useEquipmentWithoutSkidNavigation";
import East from "@mui/icons-material/East";
import {usePowerStationNavigationToAlarmsPage} from "../../../../Shared/Hooks/usePowerStationNavigationToAlarmsPage";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AlarmSeverityPill from "../../../../Utils/AlarmSeverityPill"

const cloneGridColumns = (columns: ADataGridColumn[]): ADataGridColumn[] =>
    columns.map(column => ({
        ...column,
        breakpoints: column.breakpoints ? { ...column.breakpoints } : column.breakpoints,
        filter: column.filter ? { ...column.filter } : undefined,
    }));

export const initColumnsData = [
    {
        name: 'id',
        label: 'ID',
        align: 'left',
        visibility: false,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'severityName',
        label: 'Severidade',
        align: 'left',
        minWidth: '1vw',
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "severityName",
            condition: "contains",
            value: "",
            sort: "none"
        },
    },
    {
        name: 'description',
        label: 'Descrição',
        align: 'left',
        minWidth: '15vw',
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'macroRegionName',
        label: 'macro',
        align: 'left',
        visibility: false,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'regionName',
        label: 'região',
        align: 'left',
        visibility: false,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'equipmentName',
        label: 'Equipamento',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'dateTime',
        label: 'Início',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "dateTime",
            condition: "starts-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'clearDate',
        label: 'Finalizado',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "clearDate",
            condition: "starts-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'acknowledgementDate',
        label: 'Reconhecido',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {
            name: "acknowledgementDate",
            condition: "starts-at",
            value: "",
            sort: "none",
            placeholder: 'dd/MM/yyyy HH:mm:ss'
        }
    },
    {
        name: 'userName',
        label: 'Usuário',
        align: 'left',
        visibility: true,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
] as ADataGridColumn[]

export type ActionConfig = {
    actionName: string;
    disabled: boolean;
    props?: any;
}

export interface AlarmRow {
    id: string;
    teleObjectId: number;
    teleObjectInhibited: boolean;
    severityId: number;
    severityName: string | ReactElement;
    severityColor: string;
    description: string;
    powerStationId: number;
    powerStationName: string | ReactElement;
    skidId: number;
    skidName: string | ReactElement;
    equipmentId: number;
    equipmentName: string | ReactElement;
    clearDate?: Date | string;
    acknowledgementDate?: Date | string;
    dateTime: Date | string;
    userName: string;
    hasAlarmSound: boolean;

}

const POWER_STATION_ACTIVE_ALARM_PAGE_SIZE_KEY = 'power-station-active-alarm-page-size';
const DEFAULT_PAGE_SIZE = 20;

/** Canonical: equipment-scoped. Alias: inverter-simple (same column matrix). */
export type ActiveAlarmsColumnPreset = 'unifilar' | 'equipment-scoped' | 'inverter-simple';

export type ActiveAlarmsProps = {
    powerPlantId: number;
    equipmentId?: number;
    columnPreset?: ActiveAlarmsColumnPreset;
};

export const isEquipmentScopedPreset = (preset?: ActiveAlarmsColumnPreset): boolean =>
    preset === 'equipment-scoped' || preset === 'inverter-simple';

export const buildActiveAlarmsColumns = (preset?: ActiveAlarmsColumnPreset): ADataGridColumn[] => {
    const columns = cloneGridColumns(initColumnsData);
    if (!isEquipmentScopedPreset(preset)) {
        return columns;
    }
    // ADataGridColumn tipa minWidth como number, mas o grid (e initColumnsData) já usam valores CSS
    // como '15vw'/'30vw' em runtime — manter o alargamento da Descrição do preset.
    return columns.map((column) => {
        if (column.name === 'skidName' || column.name === 'equipmentName') {
            return { ...column, visibility: false };
        }
        if (column.name === 'description') {
            return { ...column, minWidth: '30vw' as unknown as number };
        }
        return column;
    });
};

export const mergeEquipmentIdFilter = (
    filters: ADataGridFilter[],
    equipmentId?: number
): ADataGridFilter[] => {
    const withoutEquipment = filters.filter((f) => f.name !== 'equipmentId');
    if (equipmentId != null && Number.isFinite(equipmentId) && equipmentId > 0) {
        withoutEquipment.push({
            name: 'equipmentId',
            condition: 'equals',
            value: equipmentId.toString(),
            sort: 'none',
        });
    }
    return withoutEquipment;
};

/**
 * FR-016 / 025: em modo equipment-scoped, não hidratar entities de outro equipamento/usina.
 * Unifilar (scopedEquipmentId ausente) = passthrough.
 */
export const filterEntitiesForActiveAlarmsContext = (
    entities: Array<Alarm> | null | undefined,
    scopedEquipmentId?: number,
    powerPlantId?: number
): Array<Alarm> => {
    const list = isArray(entities) ? entities : [];
    if (scopedEquipmentId == null) {
        return list;
    }
    return list.filter((alarm) => {
        if (alarm.equipmentId !== scopedEquipmentId) {
            return false;
        }
        if (powerPlantId != null && Number.isFinite(powerPlantId)) {
            return alarm.powerStationId === powerPlantId;
        }
        return true;
    });
};

const getInitialPageSize = (): number => {
    const storedSize = Number(Storage.local.get(POWER_STATION_ACTIVE_ALARM_PAGE_SIZE_KEY, DEFAULT_PAGE_SIZE));
    if (Number.isFinite(storedSize) && storedSize > 0) {
        return storedSize;
    }
    return DEFAULT_PAGE_SIZE;
}

const ActiveAlarms: FunctionComponent<ActiveAlarmsProps> = ({
    powerPlantId,
    equipmentId,
    columnPreset = 'unifilar',
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = new QueryClient();
    const [firstLoad, setFirstLoad] = useState(true);
    const scopedEquipmentId =
        equipmentId != null && Number.isFinite(equipmentId) && equipmentId > 0
            ? equipmentId
            : undefined;
    const hasInvalidScopedEquipment =
        equipmentId != null && !(Number.isFinite(equipmentId) && equipmentId > 0);

    const isLoading = useAppSelector((state : RootState) => state.alarmPowerPlant.loading);
    const totalOfRecords = useAppSelector((state : RootState) => state.alarmPowerPlant.totalOfRecords);
    const entities: Array<Alarm> = useAppSelector((state: RootState) => state.alarmPowerPlant.entities);

    const navigationToPowerStation = usePowerStationNavigation();
    const navigationToSkid = useSkidNavigation();
    const navigationToEquipment = useEquipmentNavigation();
    const navigationToEquipmentWithoutSkid = useEquipmentWithoutSkidNavigation();
    const navigationToPowerStationAlarm = usePowerStationNavigationToAlarmsPage();

    const [gridColumns] = useState(() => buildActiveAlarmsColumns(columnPreset));
    const [rows, setRows] = useState<Array<AlarmRow>>([]);
    const [currentDataGridFilters, setCurrentDataGridFilters] = useState<Array<ADataGridFilter>>(
        extractFiltersFromColumns(gridColumns)
    );

    const getInitFilters = useCallback(() => {
        const mergedFilters = mergeEquipmentIdFilter(
            mergeFiltersWithTotalizerAndPowerStation(
                extractFiltersFromColumns(initColumnsData),
                "active",
                powerPlantId
            ),
            scopedEquipmentId
        );
        return new PagedSearchParams(
            mergedFilters,
            0,
            getInitialPageSize()
        )
    }, [powerPlantId, scopedEquipmentId]);
    const [pagedSearchParams, setPagedSearchParams] = useState(getInitFilters);
    const [hasAlarmSound, setHasAlarmSound] = useState(false);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const [isSearch, setIsSearch] = useState(false)

    const formatISOData = (date: string) => {
        const dateTimeString =  validateDateFormatISO(date);
        if(isValid(new Date(dateTimeString))){
            return LocalDateTimeFormatISO(dateTimeString)
        }
        if(isValid(new Date(date))){
            return LocalDateTimeFormatISO(date)
        }
        enqueueSnackbar("Data invalida!", {variant: 'error'})
        return "";
    }

    const formatDateFilters = (params: PagedSearchParams): PagedSearchParams => {
        const dateFilterNames = ['dateTime', 'clearDate', 'acknowledgementDate'];
        const filters = params.filters.map(filter => {
            if(dateFilterNames.includes(filter.name) && filter.value.length > 0) {
                return {
                    ...filter,
                    value: formatISOData(filter.value)
                };
            }
            return filter;
        });

        return new PagedSearchParams(filters, params.page, params.size);
    }

    const handleNavigationPowerPlant = (event:  React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPowerStation(powerPlantId);
    }

    const handleNavigationSkid = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>, powerStationId: number, skidId: number): void => {
        event.preventDefault();
        navigationToSkid(powerStationId, skidId);
    }

    const handleNavigationEquipmentPage = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>, powerPlantId: number, skidId: number | null, equipmentId: number): void => {
        event.preventDefault();
        if(powerPlantId != null && skidId != null){
            navigationToEquipment(equipmentId);
        } else {
            navigationToEquipmentWithoutSkid(equipmentId);
        }

    }

    const isActiveUnacknowledgedAlarm = (alarm: Alarm): boolean => {
        const isNotCleared = alarm?.clearDate == null || alarm?.clearDate.length < 1;
        const isNotAcknowledged = alarm?.acknowledgementDate == null || alarm?.acknowledgementDate.length < 1;
        return isNotCleared && isNotAcknowledged && alarm.hasAlarmSound;
    };

    const structureRow = (entitiesValues: Array<Alarm>) => {
        let newHasAlarmSound = false;
        let alarms: Array<AlarmRow> = []
        entitiesValues.forEach( alarm => {
            alarms.push({
                teleObjectId: alarm.teleObjectId,
                teleObjectInhibited: alarm.teleObjectInhibited,
                id: alarm.id.toString(),
                severityId: alarm.severityId,
                severityName: alarm.teleObjectInhibited
                    ? <AlarmSeverityPill
                        tone={INHIBITED_ALARM_COLOR}
                        label={getAlarmDisplayLabel(alarm)}
                        isSound={isActiveUnacknowledgedAlarm(alarm)}
                    />
                    : isActiveUnacknowledgedAlarm(alarm) ?
                    <AlarmSeverityPill tone={alarm.severityColor} label={alarm.severityName} isSound={alarm.hasAlarmSound}/>
                    : <AlarmSeverityPill tone={alarm.severityColor} label={alarm.severityName}/>,
                severityColor: alarm.severityColor,
                description: alarm.description,
                powerStationId: alarm.powerStationId,
                powerStationName: <Link href="#" onClick={(event) => handleNavigationPowerPlant(event, alarm.powerStationId)} underline="hover">{alarm.powerStationName}</Link>,
                skidId: alarm.skidId,
                skidName:<Link href="#" onClick={(event) => handleNavigationSkid(event, alarm.powerStationId, alarm.skidId)} underline="hover">{alarm.skidName}</Link>,
                equipmentId: alarm.equipmentId,
                equipmentName: <Link href="#" onClick={(event) => handleNavigationEquipmentPage(event, alarm.powerStationId, alarm.skidId, alarm.equipmentId)}>{alarm.equipmentName}</Link>,                clearDate: alarm.clearDate ? DateFormat(alarm.clearDate) : "",
                acknowledgementDate: alarm.acknowledgementDate ? DateFormat(alarm.acknowledgementDate) : "",
                dateTime: DateFormat(alarm.dateTime),
                userName: alarm.userName,
                hasAlarmSound: alarm.hasAlarmSound,
            });
            if( (alarm?.clearDate == null || alarm?.clearDate.length < 1) && (alarm?.acknowledgementDate == null || alarm?.acknowledgementDate.length < 1) && alarm?.hasAlarmSound === true && !newHasAlarmSound){
                newHasAlarmSound = true;
            }
        });
        setHasAlarmSound(newHasAlarmSound);
        setRows(alarms);
    }

    const alarmSound = async () => {
        const audio = new Audio(process.env.PUBLIC_URL + "/notification_02.mp3");
        audio.volume = 0.7;
        const audioMuted = localStorage.getItem("audioMuted") === "true";
        try {
            await new Promise((resolve) => {

                audio.onended = resolve;
                audio.oncanplay = () => {

                    audio.muted = !audioMuted;
                    if(audioMuted){
                        audio.play();
                    }
                };
            });
        } catch (err) {
            console.log("error", err);
        }
    }

    // Effects
    const columnsRef = useRef(false);
    const paramsRef = useRef(false);
    const pendingSearchRef = useRef(false);

    const isEmptyTable = () => {
        const filtered = filterEntitiesForActiveAlarmsContext(
            entities,
            scopedEquipmentId,
            powerPlantId
        );
        return filtered.length === 0;
    }

    const ensureContextFilters = (params: PagedSearchParams): PagedSearchParams => {
        let filters = params.filters.filter(
            (f) => f.name !== 'powerStationId' && f.name !== 'equipmentId'
        );

        filters = [
            { name: 'powerStationId', condition: 'equals', value: powerPlantId.toString(), sort: 'none' },
            ...filters,
        ];

        if (scopedEquipmentId != null) {
            filters.push({
                name: 'equipmentId',
                condition: 'equals',
                value: scopedEquipmentId.toString(),
                sort: 'none',
            });
        }

        return new PagedSearchParams(filters, params.page, params.size);
    };

    const executeSearch = useCallback((params: PagedSearchParams) => {
        if (hasInvalidScopedEquipment) {
            setRows([]);
            setHasAlarmSound(false);
            return;
        }
        const formattedParams = formatDateFilters(params);
        const safeParams = ensureContextFilters(formattedParams);
        dispatch(searchEntities({ params: safeParams }));
    }, [dispatch, powerPlantId, scopedEquipmentId, hasInvalidScopedEquipment]);

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        Storage.local.set(POWER_STATION_ACTIVE_ALARM_PAGE_SIZE_KEY, pageSize);
        setCurrentDataGridFilters(dataGridFilters);
        if(dataGridFilters && dataGridFilters.length > 0){
            setIsSearch(dataGridFilters.some(filter => filter.value !== ''))
        }
        const mergedFilters = mergeEquipmentIdFilter(
            mergeFiltersWithTotalizerAndPowerStation(dataGridFilters, "active", powerPlantId),
            scopedEquipmentId
        );
        const newParams = new PagedSearchParams(mergedFilters, page, pageSize);
        setPagedSearchParams(newParams);
        executeSearch(newParams);
    }, [powerPlantId, scopedEquipmentId, executeSearch]);

    useInterval(() => {
        if (!isLoading && !hasInvalidScopedEquipment) {
            executeSearch(pagedSearchParams);
        }
    }, userConfig.alarmUpdateInterval); //1 minute

    useEffect(() => {
        // Reset list when plant/equipment context changes (avoid leaking rows / FR-016)
        setRows([]);
        setHasAlarmSound(false);
        dispatch(clear());
        setPagedSearchParams(getInitFilters());
    }, [powerPlantId, scopedEquipmentId, getInitFilters, dispatch]);

    useEffect(() => {
        if (hasInvalidScopedEquipment) {
            setRows([]);
            setHasAlarmSound(false);
            return;
        }
        if(paramsRef.current) {
            if (isLoading) {
                pendingSearchRef.current = true;
                return;
            }

            pendingSearchRef.current = false;
            executeSearch(pagedSearchParams);
        } else {
            paramsRef.current = true;
        }
    }, [powerPlantId, scopedEquipmentId, pagedSearchParams, executeSearch, hasInvalidScopedEquipment]);

    useEffect(() => {
        if (!isLoading && pendingSearchRef.current && !hasInvalidScopedEquipment) {
            pendingSearchRef.current = false;
            executeSearch(pagedSearchParams);
        }
    }, [isLoading, pagedSearchParams, executeSearch, hasInvalidScopedEquipment]);

    useEffect(() => {
        if (hasAlarmSound) {
            const intervalo = setInterval(() => {
                alarmSound();
            }, 2000);

            return () => clearInterval(intervalo);
        }
    },[hasAlarmSound]);

    useEffect(() => {
        if(!columnsRef.current){
            columnsRef.current = true;
        }
    },[gridColumns]);

    useEffect(() => {
        if (hasInvalidScopedEquipment) {
            setRows([]);
            setHasAlarmSound(false);
            setFirstLoad(false);
            return;
        }

        const filtered = filterEntitiesForActiveAlarmsContext(
            entities,
            scopedEquipmentId,
            powerPlantId
        );
        if (filtered.length > 0) {
            structureRow(filtered);
        } else {
            setRows([]);
            setHasAlarmSound(false);
        }
        setFirstLoad(false);
    }, [entities, hasInvalidScopedEquipment, scopedEquipmentId, powerPlantId]);

    const newHandleOnColor = (data: any, columnItem: any): ADataGridColors => {
        return handleOnColorWithPill(data,columnItem,theme);
    }

    return (
        <QueryClientProvider client={queryClient}>
            <Box sx={{mt: 3}}>
                <ASectionTitle
                    titleLabel={"Alarmes Ativos"}
                    styleContainer={{padding: '0px'}}
                    styleContainerTitle={{padding: '0px'}}
                    contentChildrenStyle={{padding: 0}}
                    contentChildren={
                        <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={(event) => {navigationToPowerStationAlarm(powerPlantId)}}
                            startIcon={<East />}
                            sx={{
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: 'transparent'
                                },
                                display: 'flex',
                                justifyContent: 'right',
                            }}
                        >
                            IR PARA TELA DE ALARMES
                        </Button>
                    }
                />
                <ACard
                    styleProps={{
                        cardStyle: {margin: 0, padding: 0, marginTop: 2, borderRadius: 0, borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px'},
                        headerStyle: {margin: 0, padding: 0, height: 0},
                        contentStyle: {margin: 0, padding: 0},
                    }
                    }>
                    <ADataGrid
                        tableContainerStyle={{borderRadius: 0}}
                        noMaxHeight
                        hideFilters={false}
                        showFilterOptions={false}
                        hideSelection={true}
                        disablePagination={isEmptyTable()}
                        size={"small"}
                        columns={gridColumns}
                        rows={rows}
                        page={pagedSearchParams.page}
                        loading={isLoading && firstLoad}
                        totalOfRecords={totalOfRecords || 0}
                        rowsPerPage={pagedSearchParams.size}
                        onFiltersChange={handleOnFiltersChange}
                        onColorSelect={newHandleOnColor}
                        actionColumnWidth={210}
                        actionColumnStyle={{minWidth: 200}}
                        disableActionsHeaderIcons={true}
                    />
                    {
                        (isEmptyTable() &&
                            <EmptyState
                            title={isSearch ? "Alarmes não localizados" : "Sem alarmes ativos"}
                            description={isSearch ? "Ajuste os critérios de busca utilizados e tente novamente." : "Não existem alarmes ativos no momento"}
                            icon={isSearch ? <SearchOutlinedIcon/> : <CheckCircleOutlineIcon />}
                            />
                        )
                    }
                </ACard>
            </Box>
        </QueryClientProvider>
    )
}

export default ActiveAlarms;
